/**
 * Todo el copy de la landing, en un solo sitio.
 *
 * Los componentes no llevan texto hardcodeado: así el equipo de contenido edita
 * este archivo sin tocar JSX, y queda un único punto de partida el día que se
 * añada i18n. El texto es el del prototipo aprobado (design/) — verbatim.
 */

export interface NavLink {
  readonly label: string;
  readonly href: string;
}

export interface RoleCard {
  readonly kicker: string;
  readonly title: string;
  readonly description: string;
  readonly benefits: readonly string[];
  readonly cta: string;
  readonly href: string;
}

export interface Step {
  readonly number: string;
  readonly title: string;
  readonly description: string;
}

export interface Feature {
  readonly number: string;
  readonly title: string;
  readonly description: string;
  /** Controla ancho (bento asimétrico) y tratamiento visual de la card. */
  readonly variant: 'dark' | 'outline' | 'muted';
  readonly wide: boolean;
}

/**
 * Destinos del portal.
 *
 * Centralizados porque hoy son marcadores: cámbielos por las URLs reales del
 * registro/portal antes de publicar. Si apuntan a otro origen, revise también
 * `isExternal` en components/ui/Button.tsx — los enlaces externos salen
 * automáticamente con rel="noopener noreferrer".
 *
 * Mientras sigan siendo marcadores, `pendingRoutes` (abajo) los declara como
 * pendientes y cualquier clic muestra el aviso de `comingSoon` en lugar de
 * navegar a un 404.
 */
export const routes = {
  portal: '/portal',
  signup: '/registro',
  signupSolicitante: '/registro?rol=solicitante',
  signupPostulante: '/registro?rol=postulante',
  terminos: '/terminos',
  privacidad: '/privacidad',
} as const;

/**
 * Destinos que todavía no existen.
 *
 * Ésta es la única lista que hay que tocar cuando una pantalla entre en
 * producción: se saca de aquí y el enlace empieza a navegar de verdad, sin
 * cambiar ni un componente. Ver src/lib/pending.ts.
 */
export const pendingRoutes: readonly string[] = [
  routes.portal,
  routes.signup,
  routes.signupSolicitante,
  routes.signupPostulante,
  routes.terminos,
  routes.privacidad,
];

/**
 * Aviso de «funcionalidad en desarrollo».
 *
 * Las frases rotan en cada clic — es el huevo de pascua: quien insista se lleva
 * una respuesta distinta cada vez, y al agotarlas, un guiño a su persistencia.
 */
export const comingSoon = {
  badge: 'EN DESARROLLO',
  /** `{accion}` se sustituye por el texto del botón pulsado. */
  bodyTemplate: '«{accion}» apunta a una pantalla que aún estamos construyendo.',
  quips: [
    'Los andamios están puestos; el portal llega pronto.',
    'Este botón ya existe. Su destino todavía no.',
    'El equipo está en ello, café mediante.',
    'Nada se ha roto: la funcionalidad sigue en desarrollo.',
    'Guarde la página. Volverá y esto funcionará.',
  ],
  /** `{veces}` se sustituye por el número de intentos. */
  insistTemplate:
    '{veces} intentos. Seguimos en desarrollo, pero admiramos la constancia.',
  dismiss: 'Cerrar aviso',
} as const;

export const nav = {
  links: [
    { label: 'Propuesta de Valor', href: '#propuesta' },
    { label: 'Cómo Funciona', href: '#como-funciona' },
    { label: 'Beneficios', href: '#beneficios' },
  ] as readonly NavLink[],
  cta: { label: 'Acceder al Portal', href: routes.portal },
} as const;

export const hero = {
  eyebrow: 'LEGALTECH · REPÚBLICA DOMINICANA',
  // El titular se parte para poder resaltar el tramo central en azul.
  titleBefore: 'El primer marketplace de ',
  titleHighlight: 'abogado a abogado',
  titleAfter: ' en República Dominicana',
  paragraph:
    'Togados permite delegar actuaciones judiciales que no requieren necesariamente la presencia personal del abogado titular, desde aplazamientos y trámites simples, hasta audiencias más serias que puedan ser cubiertas por abogados delegados cualificados, verificados y con dominio de la materia.',
  primaryCta: {
    label: 'Soy Solicitante',
    hint: 'Publicar casos',
    href: routes.signupSolicitante,
  },
  secondaryCta: {
    label: 'Soy Postulante',
    hint: 'Cubrir casos',
    href: routes.signupPostulante,
  },
  // El cliente autoriza afirmar que los abogados están verificados, pero pidió
  // no mencionar públicamente ante quién se verifica la matrícula: ese detalle
  // queda como criterio interno de admisión.
  trust: 'Abogados verificados con matrícula vigente.',
} as const;

export const valueProps = {
  eyebrow: 'PROPUESTA DE VALOR',
  title: 'Una plataforma, dos togas',
  intro:
    'Togados formaliza una práctica que hoy ocurre de manera informal: sin trazabilidad, sin contrato y sin comprobante fiscal. Ambos lados de la plataforma son profesionales del derecho.',
  cards: [
    {
      kicker: 'QUIEN DELEGA',
      title: 'El Solicitante',
      description:
        'Abogado o firma que publica la actuación y paga por ella: audiencia, diligencia, vista, depósito de documentos o notificación.',
      benefits: [
        'Cobertura territorial inmediata, sin corresponsales fijos en cada provincia.',
        'Menos aplazamientos y menos riesgo de defecto por incomparecencia.',
        'Contrato, comprobante fiscal y expediente completo de cada encargo.',
      ],
      cta: 'Publicar una actuación',
      href: routes.signupSolicitante,
    },
    {
      kicker: 'QUIEN EJECUTA',
      title: 'El Postulante',
      description:
        'Abogado corresponsal que revisa la publicación, postula por el valor acordado y ejecuta la actuación en su jurisdicción.',
      benefits: [
        'Ingresos por tiempo disponible, sin necesidad de cartera propia.',
        'Flujo de trabajo constante en los tribunales de su demarcación.',
        'Particularmente relevante para abogados jóvenes y de provincia.',
      ],
      cta: 'Cubrir actuaciones',
      href: routes.signupPostulante,
    },
  ] as readonly RoleCard[],
} as const;

export const howItWorks = {
  eyebrow: 'CÓMO FUNCIONA',
  title: 'De la publicación a la liquidación, sin improvisar',
  steps: [
    {
      number: '01',
      title: 'Publique la actuación',
      description:
        'Indique tribunal, sala, fecha, hora y el valor ofrecido. El catálogo depurado autocompleta la jurisdicción.',
    },
    {
      number: '02',
      title: 'Reciba postulaciones',
      description:
        'Abogados verificados y disponibles en esa demarcación revisan la publicación y postulan. Usted asigna.',
    },
    {
      number: '03',
      title: 'Ejecución y liquidación',
      description:
        'Confirmado el pago se habilita la mensajería para instrucciones y poderes. El corresponsal ejecuta y recibe su liquidación semanal.',
    },
  ] as readonly Step[],
} as const;

export const features = {
  eyebrow: 'FUNCIONALIDADES CLAVE',
  title: 'La infraestructura de la delegación formal',
  items: [
    {
      number: '01',
      title: 'Catálogo depurado de tribunales',
      description:
        'Directorio de tribunales, salas y direcciones con geolocalización y autocompletado. Es el eje del matching: la disponibilidad se define por jurisdicción, no solo por especialidad.',
      variant: 'dark',
      wide: true,
    },
    {
      number: '02',
      title: 'Mensajería interna cifrada',
      description:
        'Canal habilitado tras el pago: instrucciones del caso, poder de representación y documentación de soporte, en un solo hilo.',
      variant: 'outline',
      wide: false,
    },
    {
      number: '03',
      title: 'Notificaciones inteligentes',
      description:
        'Nuevas publicaciones, postulaciones, asignaciones y cambios de estado en tiempo real, con acciones directas desde la notificación.',
      variant: 'outline',
      wide: false,
    },
    {
      number: '04',
      title: 'Pagos seguros y liquidación',
      description:
        'Pasarela Azul para tarjeta de crédito, factura electrónica e-CF y liquidación semanal al abogado corresponsal. Cada actuación deja comprobante.',
      variant: 'muted',
      wide: true,
    },
  ] as readonly Feature[],
} as const;

export const finalCta = {
  title: 'Modernice su práctica legal',
  paragraph:
    'Deje atrás al colega improvisado. Delegue audiencias, trámites y aplazamientos con abogados verificados y cualificados.',
  primary: { label: 'Únete a la Red', href: routes.signup },
  secondary: { label: 'Acceder al Portal', href: routes.portal },
} as const;

export const footer = {
  tagline:
    'La delegación de actuaciones judiciales, formalizada. Marketplace profesional de abogado a abogado en República Dominicana.',
  // Artes oficiales sin modificar (Apple ES-MX, Google es-419). No recortar,
  // reteñir ni alterar proporciones: ambas guías de marca lo prohíben.
  // El badge de Google trae 29px de aire vertical incluido en el PNG.
  stores: [
    {
      label: 'Descárgalo en el App Store',
      href: '#',
      src: '/badges/app-store-es.svg',
      width: 120,
      height: 40,
      imgClass: 'h-10',
    },
    {
      label: 'Disponible en Google Play',
      href: '#',
      src: '/badges/google-play-es.png',
      width: 134,
      height: 52,
      imgClass: 'h-13 -my-1.5',
    },
  ],
  columns: [
    {
      title: 'PLATAFORMA',
      links: [
        { label: 'Propuesta de Valor', href: '#propuesta' },
        { label: 'Cómo Funciona', href: '#como-funciona' },
        { label: 'Beneficios', href: '#beneficios' },
      ],
    },
    {
      title: 'LEGAL',
      links: [
        { label: 'Términos y Condiciones', href: routes.terminos },
        { label: 'Política de Privacidad', href: routes.privacidad },
      ],
    },
  ],
  contact: {
    title: 'CONTACTO',
    email: 'info@togados.net',
    address: 'Santo Domingo, República Dominicana',
  },
  legal: '© 2026 Togados. Todos los derechos reservados.',
  // Crédito de autoría. El texto va partido porque entre medio se intercalan
  // los íconos; `loveLabel` y `coffeeLabel` son sus nombres accesibles, de modo
  // que un lector de pantalla anuncia la frase completa.
  madeBy: {
    lead: 'Hecho con',
    conjunction: 'y',
    by: 'por',
    company: 'WEPSYS SRL',
    href: 'https://wepsys.net/',
    loveLabel: 'amor',
    coffeeLabel: 'café',
  },
} as const;
